import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../App";

// Native Production Tracker — Pipeline board. Reads the live production_assets
// via /api/ptracker/pipeline (read-only) and lays them out brief -> delivered.
// Themed entirely on DM's CSS variables so Punk/Unc modes both work.

const TYPE_COLOR = {
  reel: "#3B82F6", ad_film: "#A855F7", story: "#EC4899", performance: "#10B981",
  thumbnail: "#06B6D4", static: "#22C55E", carousel: "#F59E0B",
};
const TYPE_LABEL = {
  reel: "REEL", ad_film: "AD FILM", story: "STORY", performance: "PERFORMANCE",
  thumbnail: "THUMB", static: "STATIC", carousel: "CAROUSEL",
};

const mono = "var(--font-mono)";

export default function PtrackerPipeline() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [typeF, setTypeF] = useState("");
  const [clientF, setClientF] = useState("");
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/ptracker/pipeline")
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Couldn't load the pipeline."); });
    return () => { alive = false; };
  }, []);

  const assets = data?.assets || [];
  const clients = useMemo(
    () => [...new Set(assets.map((a) => a.client).filter(Boolean))].sort(),
    [assets]
  );
  const filtered = assets.filter(
    (a) =>
      (!typeF || a.asset_type === typeF) &&
      (!clientF || a.client === clientF) &&
      (!q || `${a.title} ${a.client}`.toLowerCase().includes(q.toLowerCase()))
  );
  const byPhase = useMemo(() => {
    const m = {};
    for (const p of data?.phases || []) m[p.key] = [];
    for (const a of filtered) (m[a.phase] = m[a.phase] || []).push(a);
    return m;
  }, [filtered, data]);

  const typeCounts = Object.keys(TYPE_LABEL)
    .map((k) => ({ k, n: assets.filter((a) => a.asset_type === k).length }))
    .filter((t) => t.n > 0);

  const card = {
    background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)",
    padding: 12, cursor: "pointer", transition: "transform .15s, box-shadow .15s",
  };
  const inputStyle = {
    background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)",
    color: "var(--t1)", fontFamily: mono, fontSize: 12, padding: "6px 10px",
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1440, margin: "0 auto" }} data-testid="ptracker-pipeline">
      <div style={{ marginBottom: 4 }}>
        <h1 style={{ fontFamily: "var(--font-heading, var(--font-orb))", fontSize: 30, fontWeight: 700, color: "var(--t1)", letterSpacing: "-.01em", margin: 0, textTransform: "uppercase" }}>
          Pipeline
        </h1>
        <p style={{ fontFamily: mono, fontSize: 12, color: "var(--t3)", letterSpacing: ".04em", marginTop: 4 }}>
          {data ? `${assets.length} assets · ${clients.length} accounts — brief → delivered` : "loading…"}
        </p>
      </div>

      {err && (
        <div style={{ ...card, cursor: "default", color: "var(--r)", fontFamily: mono, fontSize: 12, marginTop: 12 }}>{err}</div>
      )}

      {data && (
        <>
          {data.counts.kpi_overdue > 0 && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--rd)", border: "1px solid rgba(227,25,55,.2)", borderRadius: "var(--radius-sm)", fontFamily: mono, fontSize: 12, color: "var(--r)", letterSpacing: ".04em" }}>
              ⚠ {data.counts.kpi_overdue} asset{data.counts.kpi_overdue > 1 ? "s" : ""} live 7+ days and still need KPIs.
            </div>
          )}

          {/* counters */}
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", padding: "10px 14px", background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", fontFamily: mono, fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase" }}>
            <span style={{ color: "var(--t1)", fontWeight: 700 }}>Total {data.counts.total}</span>
            {typeCounts.map((t) => (
              <span
                key={t.k}
                onClick={() => setTypeF(typeF === t.k ? "" : t.k)}
                style={{ color: TYPE_COLOR[t.k], cursor: "pointer", fontWeight: typeF === t.k ? 700 : 400, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: TYPE_COLOR[t.k] }} />
                {TYPE_LABEL[t.k]} {t.n}
              </span>
            ))}
            <span style={{ color: "#10B981" }}>⊙ {data.counts.live} LIVE</span>
            <span style={{ color: data.counts.overdue ? "var(--r)" : "var(--t3)" }}>⚠ {data.counts.overdue} OVERDUE</span>
          </div>

          {/* filters */}
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input style={{ ...inputStyle, width: 240 }} placeholder="Search assets, accounts…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select style={inputStyle} value={typeF} onChange={(e) => setTypeF(e.target.value)}>
              <option value="">All Types</option>
              {Object.keys(TYPE_LABEL).map((k) => <option key={k} value={k}>{TYPE_LABEL[k]}</option>)}
            </select>
            <select style={inputStyle} value={clientF} onChange={(e) => setClientF(e.target.value)}>
              <option value="">All Clients</option>
              {clients.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* kanban */}
          <div style={{ marginTop: 16, display: "flex", gap: 12, overflowX: "auto", paddingBottom: 16 }}>
            {(data.phases || []).map((p) => (
              <div key={p.key} style={{ width: 280, flexShrink: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "var(--s2)", borderRadius: "var(--radius-sm)", marginBottom: 8 }}>
                  <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--t2)", letterSpacing: ".1em" }}>{p.label.toUpperCase()}</span>
                  <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--t3)", background: "var(--s1)", borderRadius: 10, padding: "1px 8px" }}>{(byPhase[p.key] || []).length}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(byPhase[p.key] || []).map((a) => (
                    <div
                      key={a.asset_id}
                      style={card}
                      onClick={() => navigate("/production")}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,.15)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: TYPE_COLOR[a.asset_type] || "var(--t4)" }} />
                        <span style={{ fontFamily: mono, fontSize: 9, fontWeight: 700, color: TYPE_COLOR[a.asset_type] || "var(--t3)", letterSpacing: ".08em" }}>{TYPE_LABEL[a.asset_type] || a.asset_type}</span>
                        {a.overdue && <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, fontWeight: 700, color: "var(--r)", background: "var(--rd)", borderRadius: 3, padding: "0 4px" }}>OVERDUE</span>}
                        {!a.overdue && a.stage_age_days >= 5 && <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 9, fontWeight: 700, color: "#d97706" }}>{a.stage_age_days}d</span>}
                      </div>
                      <div style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, color: "var(--t1)", lineHeight: 1.35 }}>{a.title}</div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                        <span style={{ fontFamily: mono, fontSize: 9, color: "var(--t3)", background: "var(--s2)", borderRadius: 3, padding: "2px 6px", letterSpacing: ".06em", textTransform: "uppercase" }}>{a.status}</span>
                        <span style={{ fontFamily: mono, fontSize: 10, color: "var(--t3)" }}>{a.client}</span>
                      </div>
                    </div>
                  ))}
                  {(byPhase[p.key] || []).length === 0 && (
                    <div style={{ border: "1px dashed var(--bdr)", borderRadius: "var(--radius-sm)", padding: "18px 0", textAlign: "center", fontFamily: mono, fontSize: 10, color: "var(--t4)", letterSpacing: ".1em" }}>EMPTY</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
